/**
 * Executive Portal
 * 
 * For: Joanna (platform_owner, agency_admin)
 * Focus: Agency oversight, revenue, reports, AI Operations
 * 
 * This is a shell for V1.0 - will be fully built in V1.1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, DollarSign, Users, BarChart3, Shield, 
  Sparkles, TrendingUp, AlertCircle, CheckCircle2, ArrowRight,
  Calendar, Clock, FileText, Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

export default function ExecutivePortal() {
  const { user } = useAuth();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-2">
            <Building2 className="h-4 w-4" />
            Executive Portal
          </div>
          <h1 className="text-2xl font-bold mb-1">Good morning, {firstName}</h1>
          <p className="text-blue-100">Here's your agency overview for today</p>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Clients</p>
                  <p className="text-2xl font-bold">24</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Caregivers</p>
                  <p className="text-2xl font-bold">12</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue MTD</p>
                  <p className="text-2xl font-bold">$42.5K</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Compliance</p>
                  <p className="text-2xl font-bold">98%</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* AI Operations Coordinator */}
          <Card className="shadow-lg border-blue-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-blue-500" />
                AI Operations Coordinator
              </CardTitle>
              <CardDescription>Your AI-powered operations assistant</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">3 Credentials Expiring</p>
                  <p className="text-xs text-muted-foreground">CPR certifications need renewal</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Payroll Ready</p>
                  <p className="text-xs text-muted-foreground">All timesheets approved</p>
                </div>
              </div>
              <Link to="/ai-operations">
                <Button className="w-full mt-2">
                  Open AI Coordinator
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
          
          {/* Quick Links */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-500" />
                Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/payroll">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <DollarSign className="h-6 w-6 text-emerald-600 mb-2" />
                  <p className="font-medium text-sm">Payroll</p>
                </div>
              </Link>
              <Link to="/billing">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <FileText className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium text-sm">Billing</p>
                </div>
              </Link>
              <Link to="/compliance-center">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Shield className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium text-sm">Compliance</p>
                </div>
              </Link>
              <Link to="/operations-studio">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Sparkles className="h-6 w-6 text-amber-600 mb-2" />
                  <p className="font-medium text-sm">AI Studio</p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
        
        {/* Portal Shell Notice */}
        <Card className="mt-6 border-blue-200 bg-blue-50/50">
          <CardContent className="p-6 text-center">
            <Badge className="mb-4">V1.0 Preview</Badge>
            <h3 className="font-semibold text-lg mb-2">Executive Portal</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Full executive dashboard with revenue analytics, operational KPIs, 
              and executive reports coming in V1.1. For now, use the sidebar to 
              access all platform features.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
